/**
 * O vocabulário da relação institucional para as telas — os `SelectField`, os
 * rótulos e a competência.
 *
 * Mora num módulo próprio pelo mesmo motivo de `organizacao-campos.ts`: três
 * lugares consomem a mesma lista (a tela de relações, o modal compartilhado e a
 * aba Relações da ficha), e a cópia é o defeito que esta feature corrige — foi
 * assim que "Administração direta" virou sinônimo de prefeitura em três telas ao
 * mesmo tempo.
 *
 * A fonte continua sendo `@atlas/types`: aqui só se traduz o valor no par
 * `{ value, label }` e se formata o que a tabela mostra.
 */
import {
  DESCRICAO_PAPEL,
  PAPEIS,
  ROTULOS,
  ROTULO_SITUACAO_RELACAO,
  SITUACOES_RELACAO,
  type Papel,
  type SituacaoRelacao,
} from "@atlas/types";
import type { PillVariant } from "@atlas/ui/web";
import type { PontaDaRelacao, RelacaoInstitucionalDto } from "@atlas/sdk";

export const PAPEL_OPCOES = PAPEIS.map((v) => ({ value: v, label: ROTULOS.papel[v] }));
export const SITUACAO_RELACAO_OPCOES = SITUACOES_RELACAO.map((v) => ({
  value: v,
  label: ROTULO_SITUACAO_RELACAO[v],
}));

/** A frase que explica o que o papel libera — a tela a mostra sob o seletor. */
export const descricaoDoPapel = (p: Papel): string => DESCRICAO_PAPEL[p];

export const rotuloDoPapel = (p: Papel): string => ROTULOS.papel[p];

/**
 * A competência corrente, AAAAMM.
 *
 * É o default do filtro e do campo "Competência inicial" porque a pergunta que
 * a tela responde ao abrir é "o que vale hoje?" — e porque uma relação começa,
 * quase sempre, no mês em que alguém decidiu criá-la.
 */
export function competenciaCorrente(): string {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** AAAAMM válido: quatro dígitos de ano e um mês entre 01 e 12. */
export function competenciaValida(c: string): boolean {
  if (!/^\d{6}$/.test(c)) return false;
  const mes = Number(c.slice(4));
  return mes >= 1 && mes <= 12;
}

/** `202609` → `09/2026`. O operador lê a folha em mês/ano, não em AAAAMM. */
export function fmtCompetencia(c: string): string {
  return competenciaValida(c) ? `${c.slice(4)}/${c.slice(0, 4)}` : c;
}

/** `09/2026 → 12/2026`, ou `09/2026 → sem fim previsto`. */
export function fmtVigencia(r: { competenciaInicio: string; competenciaFim?: string | null }): string {
  return `${fmtCompetencia(r.competenciaInicio)} → ${r.competenciaFim ? fmtCompetencia(r.competenciaFim) : "sem fim previsto"}`;
}

/**
 * A cor da situação. `suspensa` é âmbar e não cinza de propósito: ela é um
 * estado ATIVO de bloqueio, que alguém precisa desfazer — encerrada é passado,
 * suspensa é pendência.
 */
export const VARIANTE_SITUACAO: Record<SituacaoRelacao, PillVariant> = {
  agendada: "emdia",
  vigente: "averbado",
  suspensa: "pendente",
  encerrada: "expirado",
};

/** O nome curto de uma ponta — sigla quando há, nome quando não. */
export const nomeDaPonta = (p: PontaDaRelacao): string => p.sigla || p.nomeExibicao || p.nome;

/** O nome por extenso, para o título do modal e a confirmação. */
export const nomeLongoDaPonta = (p: PontaDaRelacao): string => p.nomeExibicao || p.nome;

/**
 * A frase que descreve a relação inteira, para confirmação e para o `title`.
 *
 * Uma linha de tabela é lida em colunas; um diálogo de encerramento precisa da
 * frase completa, porque quem confirma não está mais olhando o cabeçalho.
 */
export function fraseDaRelacao(r: RelacaoInstitucionalDto): string {
  return `${nomeLongoDaPonta(r.responsavel)} ${rotuloDoPapel(r.papel).toLocaleLowerCase("pt-BR")} por ${nomeLongoDaPonta(r.atendida)}`;
}
